import { inject, Injectable } from '@angular/core';
import { ToastService } from '@ps/core-overlay';
import {
  Feasibility,
  FeasibilityAudience,
  FeasibilityCriterion,
  FeasibilityQuota,
} from '@ps/survey-utils';
import { map, Observable, tap } from 'rxjs';

import { FeasibilityApi } from '../../api/feasibility/feasibility.api';
import {
  ComputeFeasibility,
  FeasibilityPayloadMapper,
} from '../../api/feasibility/mappers/feasibility.payload.mapper';

import { FeasibilityAnalysis } from './domain/feasibility-analysis';
import { formatFeasibilityCondition } from './domain/feasibility-condition.formatter';
import { FeasibilityCriteriaMapper } from './domain/feasibility-criteria.mapper';
import { FeasibilityQuotaMapper } from './domain/feasibility-quota.mapper';

@Injectable({ providedIn: 'root' })
export class FeasibilityService {
  private readonly _api = inject(FeasibilityApi);
  private readonly _toast = inject(ToastService);

  private readonly _payloadMapper = new FeasibilityPayloadMapper();

  /**
   * Computes the feasibility of a survey.
   *
   * The feasibility is based on the qualifications and quotas of the survey.
   * When the quota is not used, it is not included in the computation.
   *
   * Notifies when it is determined that a qualification is unused.
   *
   * @param data - survey ID, user, metadata, qualifications and quotas.
   * @returns feasibility - Data built from the feasibility analysis.
   */
  public compute(data: ComputeFeasibility): Observable<Feasibility> {
    const { surveyId, user, qualifications, quotas } = data;

    const defaultQuotaCriteria = new FeasibilityCriteriaMapper(
      qualifications,
    ).map();

    const { payload, quotaSet } = this._payloadMapper.build(
      data,
      defaultQuotaCriteria,
    );

    return this._api.getFeasibility(payload).pipe(
      map((result) => FeasibilityAnalysis.from(result)),
      tap((analysis) => {
        this._notifyUnusedQualifications(
          surveyId,
          analysis,
          quotaSet,
          payload.survey_localization,
          user.email,
        );
      }),
      map((analysis) => {
        return {
          total: analysis.total,
          audience: this._buildFeasibilityAudience(
            defaultQuotaCriteria,
            analysis.currencySymbol,
          ),
          quotaGroups: new FeasibilityQuotaMapper(analysis).map(quotaSet),
          hasQuota: !!quotas.length,
        };
      }),
    );
  }

  /**
   * Warns once per computation when a qualification was excluded, then pings
   * internal support unless the buyer entered an invalid range (from > to)
   */
  private _notifyUnusedQualifications(
    surveyId: number,
    analysis: FeasibilityAnalysis,
    quotaSet: ReadonlyMap<number | string, readonly FeasibilityQuota[]>,
    localization: string,
    userEmail: string,
  ): void {
    const rows = [...quotaSet.values()].flat();
    const unusedNames = analysis.unusedQualificationNames(rows);

    if (!unusedNames.length) {
      return;
    }

    this._toast.warning(
      `The feasibility calculation could not use: ${unusedNames.join(', ')}`,
    );

    if (this._hasInvalidRange(rows)) {
      return;
    }

    const unusedCodes = [
      ...new Set(
        rows
          .filter((row) => analysis.isUnknown(row.qualificationCode, row))
          .map((row) => row.qualificationCode),
      ),
    ];

    this._api
      .notifyUnusedQualification({
        projectId: surveyId,
        user: userEmail,
        criteria: unusedNames.join(', '),
        qualifications: unusedCodes.join(', '),
        localization,
      })
      .subscribe();
  }

  private _hasInvalidRange(rows: readonly FeasibilityCriterion[]): boolean {
    return rows.some((row) => {
      const range = row.rangeSets?.[0];
      return !!range && range.from > range.to;
    });
  }

  private _buildFeasibilityAudience(
    defaultQuotaCriteria: readonly FeasibilityCriterion[],
    currency: string,
  ): FeasibilityAudience[] {
    return defaultQuotaCriteria.map((criterion) => ({
      label: criterion.qualificationName ?? '',
      value: formatFeasibilityCondition(criterion, currency),
    }));
  }
}
